import React from 'react';
import { Calendar } from 'primereact/calendar';
import { Button } from '../ui/Button';

/**
 * Shared recurrence editor for both schedulers (Scheduling Pro). Replaces the old
 * "weekly days only" (wall) / "weekly OR single one-off date" (café) controls with one
 * unified widget that also supports a true seasonal DATE RANGE — e.g. "Ramadan menu,
 * Mar 1–30, every day" or "every Friday in December".
 *
 * Controlled. value = { mode:'weekly'|'range', daysMask:int, startDate:Date|null, endDate:Date|null }.
 *   - weekly : recurs on the selected weekdays forever (start_date/end_date null).
 *   - range  : valid only within [startDate,endDate]; weekdays optional (none = every day).
 *
 * The parent maps value → API fields (days_of_week / start_date / end_date). Time-of-day
 * window + all-day handling stay in the parent — this owns recurrence only.
 */
const DEFAULT_LABELS = {
  weekly: 'Repeat weekly',
  range: 'Date range',
  from: 'From',
  to: 'To',
  daysHint: 'Pick the weekdays it repeats on.',
  rangeHint: 'Plays only within this date range. Leave weekdays empty for every day; give it a higher priority to override the weekly plan.',
  rangeDaysHint: 'Optional — limit to certain weekdays inside the range (empty = every day).',
};

export default function DateRangeMode({
  value,
  onChange,
  dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
  labels = {},
}) {
  const L = { ...DEFAULT_LABELS, ...labels };
  const v = value || { mode: 'weekly', daysMask: 0, startDate: null, endDate: null };
  const set = (patch) => onChange({ ...v, ...patch });
  const toggleDay = (i) => set({ daysMask: (v.daysMask || 0) ^ (1 << i) });

  return (
    <div className="d-flex flex-column gap-2">
      <div className="d-flex gap-2 flex-wrap">
        <Button label={L.weekly} size="small" outlined={v.mode !== 'weekly'} onClick={() => set({ mode: 'weekly' })} />
        <Button label={L.range} size="small" outlined={v.mode !== 'range'} onClick={() => set({ mode: 'range' })} />
      </div>

      {v.mode === 'range' && (
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <small style={{ color: 'var(--pp-text-secondary)', width: 36 }}>{L.from}</small>
          <Calendar value={v.startDate} onChange={(e) => set({ startDate: e.value })}
            dateFormat="yy-mm-dd" showIcon placeholder={L.from} style={{ width: 170 }} />
          <small style={{ color: 'var(--pp-text-secondary)', width: 20 }}>{L.to}</small>
          <Calendar value={v.endDate} onChange={(e) => set({ endDate: e.value })}
            dateFormat="yy-mm-dd" showIcon placeholder={L.to} minDate={v.startDate || undefined} style={{ width: 170 }} />
        </div>
      )}

      <div>
        <div className="d-flex gap-1 flex-wrap">
          {dayLabels.map((lbl, i) => (
            <Button key={lbl + i} label={lbl} size="small"
              outlined={!((v.daysMask || 0) & (1 << i))} onClick={() => toggleDay(i)} />
          ))}
        </div>
        <small className="d-block mt-1" style={{ color: 'var(--pp-text-secondary)' }}>
          {v.mode === 'range' ? L.rangeDaysHint : L.daysHint}
        </small>
        {v.mode === 'range' && (
          <small className="d-block mt-1" style={{ color: 'var(--pp-text-secondary)' }}>{L.rangeHint}</small>
        )}
      </div>
    </div>
  );
}
